import { useCallback } from 'react';
import { emit } from '@zapier/events-toolkit-browser';
import { useABTestContext } from '../../components/ABTest/ABTest';
import { usePageContext } from '../../components/Page';
import { readCookie } from '@zapier/cookies';

export enum Event {
  UserInteraction = 'web_analytics.tracking.user_interaction.UserInteractionEvent',
  PageView = 'web_analytics.tracking.page_view.PageViewEvent',
  SignupUserInteractionEvent = 'web_analytics.tracking.user_interaction.SignupUserInteractionEvent',
}

export enum EventAction {
  click = 'click',
  close = 'close',
  in_view = 'in_view',
  edit = 'edit',
  select = 'select',
}

export enum InteractionName {
  call_to_action = 'call_to_action',
  carousel_button_click = 'carousel_button_click',
  component_scrolled_into_view = 'component_scrolled_into_view',
  dismiss = 'dismiss',
  navigation = 'navigation',
  popup = 'popup',
  radio_select = 'radio_select',
  submit = 'submit',
  tab_change = 'tab_change',
  toggle = 'toggle',
  video = 'video',
}

export interface EventDetails {
  custom_tracking_fields?: Record<string, any>;
  destination_href?: string;
  event_action: EventAction;
  experiment_flag: boolean;
  experiment_name?: string;
  interaction_goal?: string;
  interaction_name: InteractionName;
  interaction_raw_text?: string;
  option_selected?: string;
}

export enum CTAType {
  block_grid_item_link = 'block_grid_item_link',
  cta_button = 'cta_button',
  cta_link = 'cta_link',
  secondary_cta_button = 'secondary_cta_button',
  zap_card_link = 'zap_card_link',
}

export interface SectionEventDetails {
  experiment_flag: boolean;
  experiment_name?: string;
  section: string;
}

export interface AccordionToggleEventDetails extends SectionEventDetails {
  title: string;
  option_selected: 'opening' | 'closing';
}

export interface CTAClickEventDetails {
  experiment_flag: boolean;
  experiment_name?: string;
  text: string;
  href: string;
  type?: CTAType;
  section?: string;
}

export interface SignupUserInteractionEventDetails {
  absolute_url: string;
  signup_funnel:
    | 'signup'
    | 'signup_intent'
    | 'signup_pre_intent'
    | 'signup_captcha_rendered'
    | 'signup_captcha_verification_failed';
  signup_type:
    | 'one_tap'
    | 'google_one_tap'
    | 'sso'
    | 'google_sso'
    | 'direct'
    | 'facebook_sso'
    | 'flab_abs'
    | 'flab_sso'
    | 'microsoft_sso';
}

export function emitPageViewEvent(eventDetails?: {
  experiment_flag?: boolean;
  experiment_name?: string;
}) {
  return emit(
    {
      system_name: 'pagebuilder',
      experiment_flag: eventDetails?.experiment_flag,
      experiment_name: eventDetails?.experiment_name,
    },
    Event.PageView,
  );
}

export function emitUserInteractionEvent(eventDetails: EventDetails) {
  return emit(
    {
      ...eventDetails,
      system_name: 'pagebuilder',
    },
    Event.UserInteraction,
  );
}

export function emitSignupUserInteractionEvent(
  eventDetails: SignupUserInteractionEventDetails,
) {
  return emit(
    {
      ...eventDetails,
      signup_location: window.location.href,
      system_name: 'pagebuilder',
    },
    Event.SignupUserInteractionEvent,
  );
}

export function emitCTAClickEvent({
  text,
  href,
  type = CTAType.cta_button,
  section,
  experiment_name,
  experiment_flag,
}: CTAClickEventDetails) {
  return emitUserInteractionEvent({
    experiment_flag,
    experiment_name,
    event_action: EventAction.click,
    interaction_name: InteractionName.call_to_action,
    interaction_raw_text: text,
    destination_href: href,
    option_selected: type,
    ...(section && {
      custom_tracking_fields: {
        section_name: section,
      },
    }),
  });
}

export function emitAccordionToggleEvent({
  section,
  title,
  option_selected,
  experiment_name,
  experiment_flag,
}: AccordionToggleEventDetails) {
  return emitUserInteractionEvent({
    experiment_flag,
    experiment_name,
    event_action: EventAction.click,
    interaction_name: InteractionName.toggle,
    interaction_raw_text: title,
    option_selected,
    custom_tracking_fields: {
      section_name: section,
    },
  });
}

export function useAvroTracking() {
  const { experimentName, activeVariant } = useABTestContext();
  const { id, variations } = usePageContext();

  // Must use a callback to get the experiment name or it will be stale inside of injectExperimentContext
  const getBuilderExperimentName = useCallback(() => {
    const builderABTestID = readCookie(`builder.tests.${id}`);

    return builderABTestID
      ? variations?.[builderABTestID]?.name || 'default'
      : '';
  }, [variations, id]);

  const injectExperimentContext = useCallback(
    <T extends (...args: any) => any>(callback: T) =>
      (
        props?: Omit<Parameters<T>[0], 'experiment_flag' | 'experiment_name'>,
      ) => {
        // If the activeVariant is a fallback, then the ABTest was not set up correctly and we do not send tracking events.
        const isValidABTest =
          !!activeVariant?.name && !activeVariant?.isFallback;

        const experiment_flag = isValidABTest || !!getBuilderExperimentName();

        const experiment_name =
          getBuilderExperimentName() ||
          `${experimentName}_${activeVariant?.name}`;
        return callback({
          ...props,
          ...(experiment_flag && {
            experiment_name,
          }),
          experiment_flag,
        });
      },
    [
      experimentName,
      activeVariant?.isFallback,
      activeVariant?.name,
      getBuilderExperimentName,
    ],
  );

  const emitUserInteractionEventCallback = useCallback(
    (args: Omit<EventDetails, 'experiment_flag' | 'experment_name'>) =>
      injectExperimentContext(emitUserInteractionEvent)(args),
    [injectExperimentContext],
  );

  const emitCTAClickEventCallback = useCallback(
    (args: Omit<CTAClickEventDetails, 'experiment_flag' | 'experment_name'>) =>
      injectExperimentContext(emitCTAClickEvent)(args),
    [injectExperimentContext],
  );

  const emitAccordionToggleEventCallback = useCallback(
    (
      args: Omit<
        AccordionToggleEventDetails,
        'experiment_flag' | 'experment_name'
      >,
    ) => injectExperimentContext(emitAccordionToggleEvent)(args),
    [injectExperimentContext],
  );

  const emitPageViewEventCallback = useCallback(
    () => injectExperimentContext(emitPageViewEvent)(),
    [injectExperimentContext],
  );

  const emitSignupUserInteractionEventCallback = useCallback(
    (args: SignupUserInteractionEventDetails) =>
      injectExperimentContext(emitSignupUserInteractionEvent)(args),
    [injectExperimentContext],
  );

  return {
    emitPageViewEvent: emitPageViewEventCallback,
    emitCTAClickEvent: emitCTAClickEventCallback,
    emitUserInteractionEvent: emitUserInteractionEventCallback,
    emitAccordionToggleEvent: emitAccordionToggleEventCallback,
    emitSignupUserInteractionEvent: emitSignupUserInteractionEventCallback,
  };
}
